import { DateTime } from "luxon";

/**
 * Parse quarter string to year and quarter number
 * @param quarterStr Format: "2024-Q1"
 * @returns {year: number, quarter: number}
 */
export const parseQuarter = (
    quarterStr: string,
): { year: number; quarter: number } => {
    const [year, q] = quarterStr.split("-");
    return {
        year: parseInt(year, 10),
        quarter: parseInt(q.replace("Q", ""), 10),
    };
};

/**
 * Compare two quarters
 * @returns -1 if q1 < q2, 0 if equal, 1 if q1 > q2
 */
export const compareQuarters = (q1: string, q2: string): number => {
    const p1 = parseQuarter(q1);
    const p2 = parseQuarter(q2);
    const val1 = p1.year * 4 + p1.quarter;
    const val2 = p2.year * 4 + p2.quarter;
    return val1 < val2 ? -1 : val1 > val2 ? 1 : 0;
};

/**
 * Get current quarter string
 */
export const getCurrentQuarter = (): string => {
    const now = DateTime.now();
    const quarter = Math.ceil(now.month / 3);
    return `${now.year}-Q${quarter}`;
};

/**
 * Generate array of all quarters for a given year
 */
export const getQuartersForYear = (year: number): string[] => {
    return ["Q1", "Q2", "Q3", "Q4"].map((q) => `${year}-${q}`);
};

/**
 * Validate if quarter is selectable
 * For 'start' mode: cannot be after endQuarter or after current quarter
 * For 'end' mode: cannot be before startQuarter or after current quarter
 */
export const isQuarterSelectable = (
    quarterStr: string,
    mode: "start" | "end",
    otherBound: string,
    currentQuarter: string,
): boolean => {
    if (compareQuarters(quarterStr, currentQuarter) > 0) {
        return false; // Future quarters not selectable
    }

    if (mode === "start") {
        return compareQuarters(quarterStr, otherBound) <= 0;
    } else {
        return compareQuarters(quarterStr, otherBound) >= 0;
    }
};

/**
 * Get quarter date range for API calls
 * Returns start and end dates for the given quarter
 */
export const getQuarterDateRange = (
    quarterStr: string,
): { start: Date; end: Date } => {
    const { year, quarter } = parseQuarter(quarterStr);
    const startMonth = (quarter - 1) * 3 + 1;
    const start = DateTime.fromObject({ year, month: startMonth, day: 1 });
    const end = start.plus({ months: 3 }).minus({ days: 1 });

    return {
        start: start.toJSDate(),
        end: end.toJSDate(),
    };
};
